// src/services/contactsImport.ts
// -----------------------------------------------------------------------------
// Pure logic for the Unleashed "Customer Contacts" CSV import (Phase 4b of the
// shelter-admin redesign — docs/workpaper-unleashed-import-shelters-users.md).
// Data in → plan out, no I/O. The ContactsImport UI feeds in the parsed CSV +
// the portal's current users/memberships and gets back exactly which ACCOUNTS
// to create (silently — John's 5 July call: no email at creation; invitations
// go out later via the Phase-4c go-live utility), which memberships to ATTACH to
// people who already exist, and which CONTACT-ONLY (no-login / Same-Login)
// memberships to add. Re-running the same file is a no-op for login contacts.
//
// Collision / no-login rules — docs/contacts-import-collision-handling-plan.md
// (finalized 5 Aug 2026). Goal: no NAMED person is silently dropped except
// @zephyreducation.com.au internal contacts and truly empty rows.
//   1. @zephyreducation.com.au email → skip (Unleashed internal), counted.
//   2. No email AND no name → drop silently, counted.
//   3. No email, has a name → NO-LOGIN contact (user_id NULL, no email), keyed on
//      (shelter + name). May be the shelter's primary contact.
//   4. Unique real email (not a staff login) → LOGIN contact, keyed on email.
//      One real person across shelters = one account, several memberships.
//      An account that ALREADY exists is recognised by its membership CONTACT
//      email, not profiles.staff_user_email — the wall (db-141/142) forces that
//      NULL on every caseworker, so keying on it made every re-run re-plan the
//      person as new and reset their password (finding A1, fixed 22 Aug 2026).
//   5. Email shared by several DIFFERENT people (told apart by name) → the FIRST
//      row in the file holds the single login; every other distinct-name person
//      becomes a "SAME LOGIN" contact (user_id NULL, carries the shared email +
//      own name). Only one active login per shared email.
//   6. Email is in the ZEPHYR STAFF LIST → every row on it is DROPPED and
//      reported as bad Unleashed data. A Zephyr person may never be a DFV
//      Service contact (THE WALL), so no login and no contact row is created.
//      Two sweeps, in this order: (1) rule 1 removes @zephyreducation.com.au in
//      bulk — warehouse@, account@ and the like; (2) this is the final check,
//      which also catches a Zephyr person on an external address (a consultant
//      is in the staff list, because a consultant holds an internal role).
//   7. Unknown shelter code → reported (with contact names) then dropped.
//   8. Each service should end with >=1 login contact; if not, report it
//      (servicesWithoutLogin).
//   - "(Role)" suffixes embedded in the name ("Petters (Manager)") are parsed out
//     into contact_label and stripped before the name-compare (so a multi-shelter
//     person isn't split into a collision).
//   - Placeholder names (Deactivated, Extra number) are dropped (stop-list).
//   - Repurposed Unleashed columns (Toll Free No. = dates, DDI = code aliases,
//     Website = stock flags) are ignored.

import { isValidEmail } from "../utils/sanitize";
import { parseCsvCells } from "./deliveryImport";

export interface ContactCsvRow {
  shelter_code: string;
  customer_name: string;
  first_name: string;
  last_name: string;
  email: string;
  primary: boolean;
  phone: string;
  mobile: string;
}

export function parseContactsCsv(text: string): { rows: ContactCsvRow[]; error: string | null } {
  const cells = parseCsvCells(text);
  if (cells.length < 2) return { rows: [], error: "No data rows found in the file." };
  const header = cells[0].map((h) => h.replace(/^\*/, "").trim());
  const idx = (name: string) => header.indexOf(name);
  if (idx("Customer Code") === -1 || idx("Email Address") === -1 || idx("Primary") === -1) {
    return {
      rows: [],
      error:
        'This doesn\'t look like the Unleashed Customer Contacts export (expected "Customer Code", "Email Address" and "Primary" columns).',
    };
  }
  const get = (r: string[], name: string) => {
    const j = idx(name);
    return j === -1 ? "" : (r[j] || "").trim();
  };
  const rows = cells.slice(1).map((r) => ({
    shelter_code: get(r, "Customer Code"),
    customer_name: get(r, "Customer Name"),
    first_name: get(r, "First Name"),
    last_name: get(r, "Last Name"),
    email: get(r, "Email Address"),
    primary: get(r, "Primary") === "Yes",
    phone: get(r, "Phone"),
    mobile: get(r, "Mobile"),
  }));
  return { rows, error: null };
}

export interface ExistingUser {
  id: string;
  // Contact-model Phase 2b: the account email is staff_user_email.
  staff_user_email?: string | null;
  role?: string | null;
}

export interface ExistingMembership {
  user_id: string | null;
  shelter_code: string;
  // Contact-only (user_id NULL) memberships carry a name — used to keep re-runs of
  // the SAME file idempotent for no-login / Same-Login contacts (keyed shelter+name).
  first_name?: string | null;
  last_name?: string | null;
  // THE WALL (db-141/142): a caseworker's login email lives HERE, on the membership
  // contact — NOT on profiles.staff_user_email, which the wall forces NULL on every
  // role='shelter' row. This is the only way the planner can recognise a LOGIN
  // account it created on an earlier run (finding A1). Already supplied by
  // listAllUserShelters(), which selects it.
  email?: string | null;
}

export interface PlannedMembership {
  shelter_code: string;
  is_primary: boolean; // user-axis: their default shelter
  // Shelter-axis: this membership should become the shelter's PRIMARY CONTACT
  // (the UI points shelters.primary_contact at it). Contact-model Phase 3: a PLAN
  // signal only — NOT the retired user_shelters.is_primary_user column.
  is_primary_user: boolean;
}

export interface PlannedAccount {
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  is_generic: boolean;
  contact_label: string | null; // role parsed from the name suffix ("(Manager)")
  phones: Array<{ label: string; number: string }>;
  primary_shelter_code: string;
  memberships: PlannedMembership[];
}

export interface PlannedAttach {
  user_id: string;
  email: string;
  shelter_code: string;
  is_primary_user: boolean;
}

// A CONTACT-ONLY membership (user_id NULL) — either a no-login contact (no email)
// or a Same-Login contact (shares an email another person holds the login for, or
// a staff-login email). Applied as a plain user_shelters row.
export interface PlannedContactOnly {
  shelter_code: string;
  first_name: string;
  last_name: string;
  contact_label: string | null;
  email: string | null; // null = no-login; set = the shared email (Same-Login)
  phone: string;
  mobile: string;
  is_primary_user: boolean; // becomes the shelter's primary contact
  same_login: boolean; // true = shares a login email; false = pure no-login
}

export interface UnknownShelterContact {
  shelter_code: string;
  name: string;
}

// A CSV row skipped because its email is not a sendable address (email-validation-plan
// §6C). Reported in the preview; nothing is created for it.
export interface InvalidEmailRow {
  shelter_code: string;
  name: string;
  email: string;
}

export interface ContactsImportPlan {
  accounts: PlannedAccount[];
  attaches: PlannedAttach[];
  noLoginContacts: PlannedContactOnly[]; // rule 3
  sameLoginContacts: PlannedContactOnly[]; // rule 5
  skippedInternal: number; // sweep 1 — @zephyreducation.com.au
  skippedNoEmailNoName: number; // no email AND no name → silent drop
  skippedPlaceholder: number; // stop-list names (Deactivated, Extra number)
  skippedStaffEmail: number; // rule 6 — CSV rows dropped because the email is a Zephyr staff login
  skippedInvalidEmail: number; // email present but not a sendable address — never stored dirty
  invalidEmails: InvalidEmailRow[]; // …listed so they can be fixed in Unleashed and re-imported
  alreadyComplete: number; // existing users who need nothing
  staffConflicts: string[]; // rule 6 — the Zephyr staff addresses found in the file (nothing imported)
  unknownShelters: string[]; // codes (backward-compat)
  unknownShelterContacts: UnknownShelterContact[]; // codes WITH the affected contact names
  servicesWithoutLogin: string[]; // known shelters that end with no login contact
}

const norm = (v: string | null | undefined) => (v || "").trim().toLowerCase();

// Placeholder / junk contact names to drop (case-insensitive, exact base-name
// match). Kept deliberately small + specific so real surnames aren't blocked;
// extend as more are found. (5 Aug 2026: "Deactivated", "Extra number".)
const PLACEHOLDER_NAMES = new Set(["deactivated", "extra number"]);

// Strip any "(...)" role suffix + trailing punctuation from one name field.
// EXPORTED because the Unleashed contact LINKER must normalise names exactly the
// same way this importer does — the import stores "Skinner" while Unleashed keeps
// "Skinner (Crisi Accommodation)", and comparing those raw never matches.
// NB: the regex needs a CLOSING bracket, so an unclosed "Green (Co-Ordinator" is
// left intact — which is fine, because then neither side is stripped and the two
// raw values still agree.
export function cleanNamePart(s: string): string {
  return (s || "")
    .replace(/\([^)]*\)/g, "")
    .replace(/[,;]+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// The role in the export is embedded in the name ("Petters (Manager)"). Pull the
// LAST parenthetical (last name first, then first name) out as contact_label.
function extractRole(first: string, last: string): string | null {
  const m = (last || "").match(/\(([^)]*)\)/) || (first || "").match(/\(([^)]*)\)/);
  const r = m ? m[1].trim() : "";
  return r || null;
}

interface ParsedName {
  first: string; // cleaned (role suffix stripped) — used for baseKey + display
  last: string;
  rawFirst: string; // the name AS-IS from Unleashed (suffix intact) — what we STORE
  rawLast: string;
  role: string | null;
  baseKey: string; // normalized role-stripped name — the compare/dedupe key
  isEmpty: boolean;
}

// Split one contact's name three ways:
//   • first/last  — cleaned (cleanNamePart), used for baseKey identity + display
//   • rawFirst/rawLast — the untouched Unleashed value, which is what we STORE on
//     the contact (so "Skinner (Crisi Accommodation)" matches the Unleashed API
//     name verbatim at GUID-link time — no fragile reverse reconstruction)
//   • role        — the suffix, parked in contact_label (portal-only, never synced)
// The cleaned name still drives dedupe/idempotency; only the stored + matched name
// is raw. (John, 25 Aug 2026 — see docs/contact-import-raw-name-and-role-plan.md.)
function parseContactName(first: string, last: string): ParsedName {
  const f = cleanNamePart(first);
  const l = cleanNamePart(last);
  const role = extractRole(first, last);
  const baseKey = `${f} ${l}`.trim().toLowerCase();
  return {
    first: f,
    last: l,
    rawFirst: (first || "").trim(),
    rawLast: (last || "").trim(),
    role,
    baseKey,
    isEmpty: !baseKey,
  };
}

function phonesFor(rows: ContactCsvRow[]): Array<{ label: string; number: string }> {
  const out: Array<{ label: string; number: string }> = [];
  const seen = new Set<string>();
  for (const r of rows) {
    for (const [label, number] of [
      ["Mobile", r.mobile],
      ["Landline", r.phone],
    ] as const) {
      const n = number.trim();
      if (n && !seen.has(n)) {
        seen.add(n);
        out.push({ label, number: n });
      }
    }
  }
  return out;
}

interface RowWithName {
  r: ContactCsvRow;
  parsed: ParsedName;
}

export function planContactsImport(
  csvRows: ContactCsvRow[],
  knownShelterCodes: string[],
  existingUsers: ExistingUser[],
  existingMemberships: ExistingMembership[],
  // Codes of shelters that already have a PRIMARY CONTACT (shelters.primary_contact
  // set). Contact-model Phase 3: SUPERSEDES reading user_shelters.is_primary_user —
  // used so the import never assigns a second primary (at most one per shelter).
  sheltersWithPrimaryContact: string[] = []
): ContactsImportPlan {
  const known = new Set(knownShelterCodes.map(norm));
  // profiles.staff_user_email is ZEPHYR STAFF ONLY (the wall, db-141/142), so this
  // map only ever matches a staff login. It drives rule 6 and nothing else.
  const usersByEmail = new Map<string, ExistingUser>();
  const usersById = new Map<string, ExistingUser>();
  for (const u of existingUsers) {
    if (u.staff_user_email) usersByEmail.set(norm(u.staff_user_email), u);
    if (u.id) usersById.set(u.id, u);
  }
  // A CASEWORKER account is recognised by its membership CONTACT email — the same
  // order getProfileByEmail uses (src/data/profiles.ts, 6 Aug 2026). Without this
  // the planner cannot see accounts it created on a previous run, so every login
  // person is re-planned as new; the create fails "already registered" and the
  // admin-users reclaim branch RESETS that person's password (finding A1).
  // Only a row that HAS a user_id holds the login: a Same-Login sibling (db-136)
  // carries the same address with user_id NULL and must never match here.
  const loginUserIdByContactEmail = new Map<string, string>();
  const membershipsByUser = new Map<string, Set<string>>();
  const sheltersWithPrimary = new Set<string>(sheltersWithPrimaryContact.map(norm));
  // Known shelters that already have at least one LOGIN membership in the portal.
  const shelterHasLogin = new Set<string>();
  // Existing CONTACT-ONLY memberships (user_id NULL) keyed on shelter+name, so a
  // re-run of the same file doesn't duplicate no-login / Same-Login contacts.
  const existingContactOnlyKeys = new Set<string>();
  for (const m of existingMemberships) {
    if (m.user_id) {
      const set = membershipsByUser.get(m.user_id) ?? new Set<string>();
      set.add(norm(m.shelter_code));
      membershipsByUser.set(m.user_id, set);
      shelterHasLogin.add(norm(m.shelter_code));
      const contactEmail = norm(m.email);
      if (contactEmail && !loginUserIdByContactEmail.has(contactEmail)) {
        loginUserIdByContactEmail.set(contactEmail, m.user_id);
      }
    } else {
      const bk = parseContactName(m.first_name || "", m.last_name || "").baseKey;
      if (bk) existingContactOnlyKeys.add(`${norm(m.shelter_code)}|${bk}`);
    }
  }

  const plan: ContactsImportPlan = {
    accounts: [],
    attaches: [],
    noLoginContacts: [],
    sameLoginContacts: [],
    skippedInternal: 0,
    skippedNoEmailNoName: 0,
    skippedPlaceholder: 0,
    skippedStaffEmail: 0,
    skippedInvalidEmail: 0,
    invalidEmails: [],
    alreadyComplete: 0,
    staffConflicts: [],
    unknownShelters: [],
    unknownShelterContacts: [],
    servicesWithoutLogin: [],
  };
  const unknown = new Set<string>();
  const unknownContactSeen = new Set<string>();
  const shelterSeen = new Map<string, string>(); // norm code -> original-case code (any kept contact)

  const noteUnknown = (code: string, name: string) => {
    unknown.add(code);
    const key = `${norm(code)}|${name.toLowerCase()}`;
    if (!unknownContactSeen.has(key)) {
      unknownContactSeen.add(key);
      plan.unknownShelterContacts.push({ shelter_code: code, name: name || "(no name)" });
    }
  };
  const displayOf = (p: ParsedName) => `${p.first} ${p.last}`.trim() || "(no name)";

  // --- First pass: bucket every row. -----------------------------------------
  const usableRows: Array<RowWithName & { email: string }> = [];
  const noLoginRows: RowWithName[] = [];
  for (const r of csvRows) {
    const e = norm(r.email);
    const parsed = parseContactName(r.first_name, r.last_name);
    if (e.endsWith("@zephyreducation.com.au")) {
      plan.skippedInternal += 1;
      continue;
    }
    if (parsed.baseKey && PLACEHOLDER_NAMES.has(parsed.baseKey)) {
      plan.skippedPlaceholder += 1;
      continue;
    }
    // An email that is present but unsendable is never imported dirty (the 15/09/2026
    // CRM failure came from exactly such a value). Counted + listed for fixing at source.
    if (e && !isValidEmail(e)) {
      plan.skippedInvalidEmail += 1;
      plan.invalidEmails.push({
        shelter_code: r.shelter_code,
        name: displayOf(parsed),
        email: (r.email || "").trim(),
      });
      continue;
    }
    if (!e) {
      if (parsed.isEmpty) {
        plan.skippedNoEmailNoName += 1;
        continue;
      }
      noLoginRows.push({ r, parsed });
      continue;
    }
    usableRows.push({ r, parsed, email: e });
  }

  // --- Contact-only helper (no-login + Same-Login share the apply shape). -----
  const addContactOnly = (
    person: RowWithName[],
    opts: { email: string | null; sameLogin: boolean }
  ) => {
    const named = person.find((x) => !x.parsed.isEmpty) ?? person[0];
    const seenShelter = new Set<string>();
    for (const { r } of person) {
      if (!r.shelter_code) continue;
      if (!known.has(norm(r.shelter_code))) {
        noteUnknown(r.shelter_code, displayOf(named.parsed));
        continue;
      }
      // Already present as a contact-only membership → idempotent re-run, skip.
      if (
        named.parsed.baseKey &&
        existingContactOnlyKeys.has(`${norm(r.shelter_code)}|${named.parsed.baseKey}`)
      ) {
        plan.alreadyComplete += 1;
        continue;
      }
      if (seenShelter.has(norm(r.shelter_code))) continue;
      seenShelter.add(norm(r.shelter_code));
      shelterSeen.set(norm(r.shelter_code), r.shelter_code);
      const wantPrimary = r.primary && !sheltersWithPrimary.has(norm(r.shelter_code));
      if (wantPrimary) sheltersWithPrimary.add(norm(r.shelter_code));
      const entry: PlannedContactOnly = {
        shelter_code: r.shelter_code,
        first_name: named.parsed.rawFirst,
        last_name: named.parsed.rawLast,
        contact_label: named.parsed.role,
        email: opts.email,
        phone: r.phone || "",
        mobile: r.mobile || "",
        is_primary_user: wantPrimary,
        same_login: opts.sameLogin,
      };
      if (opts.sameLogin) plan.sameLoginContacts.push(entry);
      else plan.noLoginContacts.push(entry);
    }
  };

  // --- Login helper (create account OR attach existing), scoped to ONE person. -
  const planLoginForPerson = (person: RowWithName[], email: string, existing?: ExistingUser) => {
    const named = person.find((x) => !x.parsed.isEmpty);
    const codes: string[] = [];
    const primaryAt = new Set<string>();
    for (const { r } of person) {
      if (!r.shelter_code) continue;
      if (!known.has(norm(r.shelter_code))) {
        noteUnknown(r.shelter_code, named ? displayOf(named.parsed) : "(no name)");
        continue;
      }
      if (!codes.some((c) => norm(c) === norm(r.shelter_code))) codes.push(r.shelter_code);
      if (r.primary) primaryAt.add(norm(r.shelter_code));
    }
    if (codes.length === 0) return; // every membership unknown — nothing to do
    for (const c of codes) {
      shelterSeen.set(norm(c), c);
      shelterHasLogin.add(norm(c)); // this person can log in at c
    }

    if (existing) {
      const have = membershipsByUser.get(existing.id) ?? new Set<string>();
      const missing = codes.filter((c) => !have.has(norm(c)));
      if (missing.length === 0) {
        plan.alreadyComplete += 1;
        return;
      }
      for (const code of missing) {
        const wantPrimaryUser = primaryAt.has(norm(code)) && !sheltersWithPrimary.has(norm(code));
        if (wantPrimaryUser) sheltersWithPrimary.add(norm(code));
        plan.attaches.push({
          user_id: existing.id,
          email,
          shelter_code: code,
          is_primary_user: wantPrimaryUser,
        });
      }
      return;
    }

    // New account. Name/details come from the first row that has a name. We store
    // the RAW name (suffix intact) so it matches the Unleashed contact verbatim at
    // GUID-link time; the role suffix is captured separately into contact_label.
    const first = named ? named.parsed.rawFirst : "";
    const last = named ? named.parsed.rawLast : "";
    const role = named ? named.parsed.role : null;
    const isGeneric = !first && !last;
    const primaryShelter = codes.find((c) => primaryAt.has(norm(c))) || codes[0];
    const displayName = isGeneric ? `${primaryShelter} — Shared` : `${first} ${last}`.trim();
    plan.accounts.push({
      email,
      first_name: first,
      last_name: last,
      display_name: displayName,
      is_generic: isGeneric,
      contact_label: role,
      phones: phonesFor(person.map((x) => x.r)),
      primary_shelter_code: primaryShelter,
      memberships: codes.map((code) => {
        const wantPrimaryUser = primaryAt.has(norm(code)) && !sheltersWithPrimary.has(norm(code));
        if (wantPrimaryUser) sheltersWithPrimary.add(norm(code));
        return {
          shelter_code: code,
          is_primary: norm(code) === norm(primaryShelter),
          is_primary_user: wantPrimaryUser,
        };
      }),
    });
  };

  // --- Usable-email rows: group by email, then split by distinct person. ------
  const byEmail = new Map<string, Array<RowWithName & { email: string }>>();
  for (const x of usableRows) {
    const list = byEmail.get(x.email) ?? [];
    list.push(x);
    byEmail.set(x.email, list);
  }

  for (const [email, items] of byEmail) {
    // Rule 6 — THE ZEPHYR STAFF LIST IS THE TEST. profiles.staff_user_email is
    // staff-only (the wall), so this map holds exactly the Zephyr staff logins —
    // consultants included, because a consultant is a Zephyr person with an
    // internal role. Sweep 1 (rule 1) already removed @zephyreducation.com.au in
    // bulk; this is the final check, and it is what catches a Zephyr person on an
    // external address.
    const staffUser = usersByEmail.get(email);
    const isZephyrStaff = !!staffUser && staffUser.role !== "shelter";
    // The account that already holds this email: the caseworker found via its
    // membership contact (A1), else the staff row (a legacy profile that still
    // carries an account email).
    const memberUserId = loginUserIdByContactEmail.get(email);
    const existing = memberUserId
      ? (usersById.get(memberUserId) ?? { id: memberUserId })
      : staffUser;

    // Distinct people on this email, in file order (role-stripped name).
    const persons: RowWithName[][] = [];
    const personIdx = new Map<string, number>();
    for (const x of items) {
      const key = x.parsed.baseKey || "(unnamed)";
      if (!personIdx.has(key)) {
        personIdx.set(key, persons.length);
        persons.push([]);
      }
      persons[personIdx.get(key)!].push(x);
    }

    if (isZephyrStaff) {
      // The address is in the Zephyr staff list, so it belongs to a Zephyr person —
      // who may NEVER be a DFV Service contact (THE WALL). Every row on it is
      // KNOCKED OUT: no login, no contact record, nothing about the staff account
      // written anywhere. It is reported as bad Unleashed data — a Zephyr person
      // recorded as a customer contact — to be fixed at source in Unleashed.
      // (Before 22 Aug 2026 these became locked "Same-Login" contacts, which is
      // exactly the row the wall forbids; db-170 now refuses them at the database.)
      plan.staffConflicts.push(email);
      plan.skippedStaffEmail += items.length;
      continue;
    }

    // Rule 5: first distinct person holds the login; the rest are Same-Login.
    persons.forEach((p, i) => {
      if (i === 0) planLoginForPerson(p, email, existing);
      else addContactOnly(p, { email, sameLogin: true });
    });
  }

  // --- No-email named rows → no-login contacts (rule 3). ----------------------
  // Group by distinct person so phones fold together; one contact per shelter.
  const noLoginByPerson = new Map<string, RowWithName[]>();
  for (const x of noLoginRows) {
    const key = x.parsed.baseKey;
    const list = noLoginByPerson.get(key) ?? [];
    list.push(x);
    noLoginByPerson.set(key, list);
  }
  for (const person of noLoginByPerson.values()) {
    addContactOnly(person, { email: null, sameLogin: false });
  }

  // --- Reports. ---------------------------------------------------------------
  plan.unknownShelters = Array.from(unknown).sort();
  plan.servicesWithoutLogin = Array.from(shelterSeen.entries())
    .filter(([n]) => !shelterHasLogin.has(n))
    .map(([, code]) => code)
    .sort();
  return plan;
}

// Random password for silently-created accounts (John, 5 July 2026: no email
// at creation — the Phase-4c invite utility sends password-reset links at
// go-live, so nobody ever needs to know this value). Meets the FULL Supabase
// password policy — ≥12 chars + at least one lowercase, uppercase, digit AND
// symbol — with ~128 bits of entropy (22 random chars). The random body is
// letters+digits only, so the 4-char prefix guarantees all four character-class
// gates (the trailing "!" supplies the required symbol — added 2026-08-10 after a
// rehearsal import failed on the newly-strict "symbol required" policy).
export function generateImportPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint32Array(22);
  crypto.getRandomValues(bytes);
  let out = "";
  for (const b of bytes) out += alphabet[b % alphabet.length];
  return `Zi9!${out}`; // prefix guarantees lower + upper + digit + SYMBOL gates
}
